import {
  ChangeEvent as ReactChangeEvent,
  FC,
  Fragment,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getEasyLayout,
  RemoteProcedureCall,
  useApplicationStateLoader,
  useApplicationStateValue,
} from '@resistdesign/voltra/app/utils';
import { APP_ENV_CONFIG } from '../config';
import { ADMIN } from '../../api/AppRoutes/AdminRoutes/IDs';
import { User, UserListInfo } from '../../common/Types';
import { SYM } from '../Components/MaterialSymbol';
import styled from 'styled-components';
import { ProfileDetails } from './Admin/ProfileDetails';
import { AdminTools } from './Admin/AdminTools';

const {
  api: { domain: API_DOMAIN },
} = APP_ENV_CONFIG;

const ProfileDropArrowTD = styled.td`
  cursor: pointer;
`;
const {
  layout: Layout,
  areas: { Header, Content },
} = getEasyLayout(styled.div`
  flex: 1 0 auto;
  height: 100%;
`)`
  header, auto
  content, 1fr
  \\ 1fr
`;
const ProfileListContent = styled(Content)`
  overflow: auto;
`;

export type AdminProps = {
  authorization: string;
};

export const Admin: FC<AdminProps> = ({ authorization }) => {
  const [searchByIDCode, setSearchByIDCode] = useState<boolean>(false);
  const onSearchByIDCodeChanged = useCallback(
    ({ target: { checked: newValue } }: ReactChangeEvent<HTMLInputElement>) => {
      setSearchByIDCode(newValue);
    },
    []
  );
  const [openProfileMap, setOpenProfileMap] = useState<{ [key: string]: boolean }>({});
  const [searchTerms, setSearchTerms] = useState<string>('');
  const USER_LIST_RPC = useMemo<RemoteProcedureCall>(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/admin',
        authorization,
      },
      path: searchByIDCode ? '/list-users-by-id-code' : '/list-users',
    }),
    [authorization, searchByIDCode]
  );
  const { value: userListInfoValue = {} } = useApplicationStateValue(ADMIN.LIST_USERS);
  const { cursor, users: userList = [] } = userListInfoValue as UserListInfo;
  const sortLatestUserList = useMemo(
    () =>
      userList.sort(
        ({ date_created: dateCreatedA }: User, { date_created: dateCreatedB }: User) => dateCreatedB - dateCreatedA
      ),
    [userList]
  );
  const { loading: userListLoading = false, makeRemoteProcedureCall: loadUserList } = useApplicationStateLoader({
    identifier: ADMIN.LIST_USERS,
    remoteProcedureCall: USER_LIST_RPC,
    manual: true,
  });
  const onSearchInputChange = useCallback(
    ({ target: { value } }: ReactChangeEvent<HTMLInputElement>) => {
      setSearchTerms(value);
    },
    [setSearchTerms]
  );
  const onToggleProfileOpen = useCallback(({ currentTarget: { dataset } }: ReactMouseEvent<HTMLTableCellElement>) => {
    const { profileId } = dataset as Record<string, string>;

    setOpenProfileMap((prevOpenProfileMap) => ({
      ...prevOpenProfileMap,
      [profileId]: !prevOpenProfileMap[profileId],
    }));
  }, []);
  const onLoadMoreUsers = useCallback(() => {
    loadUserList('', cursor);
  }, [loadUserList, cursor]);
  const showLoadMoreButton = cursor || (!searchTerms && userList.length < 1);

  useEffect(() => {
    const tiD = setTimeout(() => {
      if (searchTerms.length > 0) {
        loadUserList(searchTerms);
      }
    }, 800);

    return () => clearTimeout(tiD);
  }, [searchTerms, loadUserList]);

  return (
    <Layout data-id="ADMIN">
      <Header>
        <input
          type="text"
          value={searchTerms}
          onChange={onSearchInputChange}
          placeholder="Search for users..."
          autoFocus
        />
        <label>
          <input type="checkbox" checked={searchByIDCode} onChange={onSearchByIDCodeChanged} />
          Search By ID Code
        </label>
      </Header>
      <ProfileListContent data-id="CONTENT">
        {userListLoading ? (
          <article aria-busy={userListLoading ? true : undefined}></article>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Show Profile</th>
                <th>Username</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
              </tr>
            </thead>
            <tbody>
              {sortLatestUserList.map((userItem, index) => {
                const { username, given_name, family_name, email, phone_number, sub } = userItem;

                return (
                  <Fragment key={`User:${index}`}>
                    <tr>
                      <ProfileDropArrowTD data-profile-id={sub} onClick={onToggleProfileOpen}>
                        <SYM>{openProfileMap[sub] ? 'arrow_drop_down' : 'arrow_right'}</SYM>
                      </ProfileDropArrowTD>
                      <td>{username}</td>
                      <td>
                        {given_name} {family_name}
                      </td>
                      <td>
                        <a href={`mailto:${email}`}>{email}</a>
                      </td>
                      <td>
                        <a href={`tel:${phone_number}`}>{phone_number}</a>
                      </td>
                    </tr>
                    {openProfileMap[sub] ? (
                      <tr>
                        <td></td>
                        <td colSpan={3}>
                          <ProfileDetails authorization={authorization} user={userItem} profileId={sub} />
                        </td>
                      </tr>
                    ) : undefined}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        {showLoadMoreButton && !searchByIDCode ? (
          <>
            <br />
            <button onClick={onLoadMoreUsers}>{cursor ? 'More...' : 'List All'}</button>
          </>
        ) : undefined}
        <hr />
        <AdminTools />
      </ProfileListContent>
    </Layout>
  );
};
